<template>
  <c-box>
    <form novalidate @submit.prevent="submit">
      <c-box
        :box-shadow="{ md: 'base' }"
        :border-radius="{ md: 'base' }"
        :mx="{ base: '-15px', md: '0' }"
        bg="white"
        :px="{ base: 'md', md: '4xl' }"
        :py="{ base: 'xl', md: '2.5rem' }"
        :mt="{ base: 'xl', md: '3rem' }"
      >
        <c-heading as="h3" size="sm" font-weight="bold">
          {{
            $t("productReturnV2.returnDetails.productSummary", {
              amount: $tc("productReturnV2.returnDetails.productAmount", totalProductCount, [
                totalProductCount,
              ]),
            })
          }}
        </c-heading>

        <c-box v-if="shipper === 'DHL'">
          <c-text :my="{ base: 'xl', md: '3xl' }" font-size="md">
            {{ $t("productReturnV2.returnDetails.dhlDropOffDateInfo") }}
          </c-text>

          <c-form-control is-required :is-invalid="inputIsInvalid('form.plannedShippingDate')">
            <c-box
              display="flex"
              :flex-direction="{ base: 'column', sm: 'row' }"
              align-items="baseline"
            >
              <c-form-label
                :min-width="{ xl: '21rem' }"
                for="requestedDeliveryDate"
                font-weight="bold"
              >
                {{ $t("productReturnV2.returnDetails.dhlDropOffDateInputLabel") }}
              </c-form-label>
              <c-box>
                <c-box>
                  <datepicker
                    id="plannedShippingDate"
                    v-chakra="
                      (theme) => ({
                        width: '100%',
                        maxWidth: '24rem',
                        '& input.form-control': {
                          cursor: 'pointer',
                          height: '3rem',
                          fontSize: `${theme.fontSizes.md} !important`,
                          background: `${theme.colors.white} url(${prepareIconForBg(
                            theme.icon.chakra.calendar.viewBox,
                            theme.icon.chakra.calendar.path,
                            theme.colors.brand.purple,
                          )}) no-repeat right 1rem center`,
                          backgroundSize: '1.25rem',
                          borderRadius: theme.radii.input,
                        },
                      })
                    "
                    v-model="form.plannedShippingDate"
                    :class="getFieldClasses('form.plannedShippingDate')"
                    input-class="form-control datepicker"
                    :monday-first="true"
                    :disabled-dates="datePickerState.disabledDates"
                    :format="customFormatter"
                    :language="getLanguageObject(user.languageCode)"
                    @selected="onFieldBlur('form.plannedShippingDate')"
                    @input="
                      onFieldBlur('form.plannedShippingDate');
                      form.plannedShippingDate = preparePlannedShippingDate(
                        form.plannedShippingDate,
                      );
                    "
                    @cleared="onFieldBlur('form.plannedShippingDate')"
                    data-cy="planned-shipping-date"
                  />
                </c-box>
                <c-box
                  v-if="isErrorField('form.plannedShippingDate')"
                  color="brand.red"
                  font-size="sm"
                  my="xs"
                  data-cy="estimated-drop-off-date-error"
                >
                  <span>
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("productReturnV2.returnDetails.dhlDropOffDateInputLabel"),
                      })
                    }}
                  </span>
                </c-box>
              </c-box>
            </c-box>
          </c-form-control>

          <c-text
            :mt="{ base: 'xl', md: '3xl' }"
            font-size="md"
            v-html="$t('productReturnV2.returnDetails.dhlDropOffDateNote')"
          />
        </c-box>
      </c-box>

      <c-box
        :box-shadow="{ md: 'base' }"
        :border-radius="{ md: 'base' }"
        :mx="{ base: '-15px', md: '0' }"
        bg="white"
        :px="{ base: 'md', md: '4xl' }"
        :py="{ base: 'xl', md: '2.5rem' }"
        :mt="{ base: 'lg', md: '3xl' }"
      >
        <c-box v-if="['POSTI', 'UPS', 'DHL', 'CONTACT_CS'].includes(shipper)">
          <c-form-control :is-required="country !== 'US'" :is-invalid="inputIsInvalid('copies')">
            <c-flex :flex-direction="{ base: 'column-reverse', sm: 'row' }" align-items="baseline">
              <c-flex
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                flex="1"
                width="100%"
              >
                <c-form-label :min-width="{ xl: '21rem' }" for="copies" font-weight="bold">
                  {{
                    country !== "US"
                      ? $t("productReturnV2.returnDetails.copiesTitle")
                      : $t("productReturnV2.returnDetails.packageTitleForUS")
                  }}
                </c-form-label>
                <c-box v-if="country !== 'US'" flex="1" mr="lg">
                  <c-input
                    id="copies"
                    v-model.number="form.copies"
                    :min="getMinValue('copies')"
                    :max="getMaxValue('copies')"
                    type="number"
                    @change="
                      form.copies = limitNumber(
                        form.copies,
                        getMinValue('copies'),
                        getMaxValue('copies'),
                        getMinValue('copies'),
                      )
                    "
                    @keyup="onFieldChange('copies')"
                    @blur="onFieldBlur('copies')"
                    data-cy="cr-return-copies"
                    :max-width="{ md: '12rem' }"
                    width="100%"
                    size="lg"
                  />

                  <c-box v-if="isErrorField('copies')" color="brand.red" font-size="sm" my="xs">
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("productReturnV2.returnDetails.copiesTitle"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-flex>
              <c-text font-size="sm" ml="auto">
                <c-text as="span" color="brand.greenDark">* </c-text>
                {{ $t("common.mandatory") }}
              </c-text>
            </c-flex>
          </c-form-control>
        </c-box>
        <c-text
          v-if="shipper === 'DHL' || shipper === 'CONTACT_CS'"
          color="gray.700"
          font-size="md"
        >
          {{ $t("productReturnV2.returnDetails.dhlNote") }}
        </c-text>

        <c-box v-if="shipper === 'UPS' || shipper === 'DHL' || shipper === 'CONTACT_CS'">
          <c-flex
            v-for="index in form.copies"
            :key="'weight_per_parcel_input' + index"
            mt="xl"
            :align-items="{ md: 'center' }"
            :flex-direction="{ base: 'column', md: 'row' }"
            :gap="{ base: 'lg', xl: '0' }"
          >
            <c-heading
              as="h5"
              font-size="md"
              font-weight="bold"
              :min-width="{ xl: '12rem', '3xl': '21rem' }"
              :mt="{
                md: form.copies === 1 && '2.5rem',
                '2xl': index === 1 ? (form.copies === 1 ? '2.7rem' : '2.5rem') : '0',
              }"
              :mb="{
                base: 'md',
                md: index === 1 && form.copies !== 1 ? 'md' : '0',
                xl: index === 1 && form.copies !== 1 ? 'lg' : '0',
                '2xl': '0',
              }"
              white-space="nowrap"
              >{{ $t("common.package") }} {{ country !== "US" ? index : "" }}</c-heading
            >
            <c-flex gap="lg" :flex-direction="{ base: 'column', '2xl': 'row' }">
              <c-flex gap="lg" :flex-direction="{ base: 'column', md: 'row' }">
                <RMACRWeightPerPackageEditor
                  :key="'return_packageWeight' + index"
                  :ref="`return_package_weight_${index}`"
                  :index="index - 1"
                  v-model="form.$weightPerPackage[index - 1]"
                  :label="`${$t('common.weight')} (${
                    useImperialUnits ? $t('common.weightType.lbs') : $t('common.weightType.kg')
                  })`"
                  @errors="onErrors(index, $event)"
                />
                <RMACRMeasurementsPerPackageEditor
                  v-if="shipper === 'DHL' || shipper === 'CONTACT_CS'"
                  :key="'return_package_measurements_' + index"
                  :ref="`return_package_measurements_${index}`"
                  v-model="form.$measurementsPerPackage[index - 1]"
                  :index="index - 1"
                  :use-imperial-units="useImperialUnits"
                  @errors="onErrors(index, $event)"
                />
              </c-flex>
              <c-button
                v-if="
                  (shipper === 'DHL' || shipper === 'CONTACT_CS') &&
                  index - 1 === 0 &&
                  limitNumber(form.copies, 1, 20, 1) > 1
                "
                :mt="{ xl: 'auto' }"
                :mb="{ xl: '2xs' }"
                :width="{
                  md: 'fit-content',
                  '2xl': 'auto',
                }"
                @click="copyFirstLineValuesToAllPackages()"
                :disabled="!isFirstLineValidAndFilled()"
                data-cy="copy-first-line-values-to-all-packages"
              >
                {{ $t("productReturnsV2.returnDetails.copyValues") }}
              </c-button>
            </c-flex>
          </c-flex>
          <!-- Errors -->
          <c-box
            v-if="anyPackageDetailAreInvalidOrErrored().length > 0"
            color="brand.red"
            font-size="sm"
            my="xs"
          >
            <c-text
              v-for="detail in anyPackageDetailAreInvalidOrErrored()"
              :key="`package_validation_error_${detail.rowNo}`"
            >
              <c-text as="span" v-if="getPackageErroredInputs(detail).length">
                {{
                  $tc(
                    "productReturnV2.returnDetails.packageErrorTitle",
                    getPackageErroredInputs(detail).length,
                    {
                      packageNo: detail.rowNo,
                      inputs: getPackageErroredInputs(detail).join(", "),
                    },
                  )
                }}
              </c-text>
            </c-text>
          </c-box>
        </c-box>
        <c-box v-else-if="shipper === 'POSTI'">
          <c-form-control is-required :is-invalid="inputIsInvalid('weight')">
            <c-box
              display="flex"
              :flex-direction="{ base: 'column', sm: 'row' }"
              align-items="baseline"
              mt="xl"
            >
              <c-form-label :min-width="{ xl: '21rem' }" for="weight">
                {{ $t("productReturn.returnDetails.weightTitle") }}
              </c-form-label>
              <c-box flex="1">
                <c-flex align-items="center" gap="md">
                  <c-input
                    id="weight"
                    v-model="form.weight"
                    :min="getMinValue('weight')"
                    :max="getMaxValue('weight')"
                    type="text"
                    @change="
                      form.weight = limitNumber(
                        form.weight,
                        getMinValue('weight'),
                        getMaxValue('weight'),
                        getMinValue('weight'),
                      )
                    "
                    @keyup="onFieldChange('weight')"
                    @blur="onFieldBlur('weight')"
                    max-width="10rem"
                    size="lg"
                    data-cy="weight"
                  />
                  <c-text font-size="md" font-weight="normal">
                    {{ $t("productReturn.returnDetails.weightType") }}
                  </c-text>
                </c-flex>

                <c-box v-if="isErrorField('weight')" color="brand.red" font-size="sm" my="xs">
                  <c-text as="span" v-if="!getValidationField('weight').required">
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("productReturn.returnDetails.weightTitle"),
                      })
                    }}
                  </c-text>
                  <c-text as="span" v-else>
                    {{
                      $t("common.error.fieldInvalid", {
                        field: $t("productReturn.returnDetails.weightTitle"),
                      })
                    }}
                  </c-text>
                </c-box>
              </c-box>
            </c-box>
          </c-form-control>
        </c-box>
      </c-box>

      <c-box
        :box-shadow="{ md: 'base' }"
        :border-radius="{ md: 'base' }"
        :mx="{ base: '-15px', md: '0' }"
        bg="white"
        :px="{ base: 'md', md: '4xl' }"
        :py="{ base: 'xl', md: '2.5rem' }"
        :mt="{ base: 'lg', md: '3xl' }"
      >
        <c-box v-if="isMaintenanceReturn" font-size="md">
          <c-heading as="h3" font-size="lg" font-weight="bold" mb="xl">
            {{ $t("productReturn.returnDetails.warrantyTitle") }}
          </c-heading>
          <c-text>{{ beautify($t("productReturn.returnDetails.warrantyFirstParagraph")) }}</c-text>
          <c-text>{{ beautify($t("productReturn.returnDetails.warrantySecondParagraph")) }}</c-text>
        </c-box>

        <c-box font-size="md">
          <c-heading
            as="h3"
            font-size="lg"
            font-weight="bold"
            :mt="isMaintenanceReturn ? 'xl' : '0'"
            mb="xl"
          >
            {{ $t("productReturn.returnDetails.returningTitle") }}
          </c-heading>
          <c-text>{{ beautify($t("productReturn.returnDetails.returningFirstParagraph")) }}</c-text>
        </c-box>

        <c-box
          v-if="isMaintenanceReturn"
          mt="xl"
          v-chakra="{
            'input[type=checkbox]+div': {
              borderRadius: 'var(--chakra-radii-input)',
              width: 'var(--chakra-sizes-6)',
              height: 'var(--chakra-sizes-6)',
            },
            'input[type=checkbox][aria-invalid=true]+div': {
              borderColor: 'brand.red',
            },
            'input[type=checkbox]:checked+div, input[type=checkbox][aria-checked=mixed]+div': {
              bg: 'brand.purple',
              borderColor: 'brand.purple',
            },
          }"
        >
          <c-checkbox
            id="confirmResetTried"
            v-model.trim="requiredFields.confirmResetTried"
            @change="onFieldBlur('requiredFields.confirmResetTried')"
            data-cy="return-confirm-reset-tried"
            :is-invalid="isErrorField('requiredFields.confirmResetTried')"
            size="lg"
            border-radius="input"
          >
            <c-text font-size="md" font-weight="bold" :ml="{ base: 'xs', sm: 'xl' }">
              {{ $t("productReturn.returnDetails.confirmResetTried") }}
              <c-link
                :href="'/files/support/' + $t('productReturn.common.resetURI')"
                is-external
                text-decoration="underline"
              >
                {{ $t("productReturn.returnDetails.resetInstructions") }}
              </c-link>
            </c-text>
          </c-checkbox>
          <c-box
            v-if="isErrorField('requiredFields.confirmResetTried')"
            color="brand.red"
            font-size="sm"
            my="xs"
          >
            {{ $t("common.mandatory") }}
          </c-box>
        </c-box>

        <c-box
          v-if="isSalesReturn"
          mt="xl"
          v-chakra="{
            'input[type=checkbox]+div': {
              borderRadius: 'var(--chakra-radii-input)',
              width: 'var(--chakra-sizes-6)',
              height: 'var(--chakra-sizes-6)',
            },
            'input[type=checkbox][aria-invalid=true]+div': {
              borderColor: 'brand.red',
            },
            'input[type=checkbox]:checked+div, input[type=checkbox][aria-checked=mixed]+div': {
              bg: 'brand.purple',
              borderColor: 'brand.purple',
            },
          }"
        >
          <c-checkbox
            id="confirmItemsAreInOriginalCondition"
            v-model.trim="requiredFields.confirmItemsAreInOriginalCondition"
            @change="onFieldBlur('requiredFields.confirmItemsAreInOriginalCondition')"
            data-cy="return-confirm-items-are-in-original-condition"
            :is-invalid="isErrorField('requiredFields.confirmItemsAreInOriginalCondition')"
            size="lg"
            border-radius="input"
          >
            <c-text font-size="md" font-weight="bold" :ml="{ base: 'xs', sm: 'xl' }">
              {{ $t("productReturn.returnDetails.confirmItemsAreInOriginalCondition") }}
            </c-text>
          </c-checkbox>
          <c-box
            v-if="isErrorField('requiredFields.confirmItemsAreInOriginalCondition')"
            color="brand.red"
            font-size="sm"
            my="xs"
          >
            {{ $t("common.mandatory") }}
          </c-box>
        </c-box>

        <c-box
          mt="xl"
          v-chakra="{
            'input[type=checkbox]+div': {
              borderRadius: 'var(--chakra-radii-input)',
              width: 'var(--chakra-sizes-6)',
              height: 'var(--chakra-sizes-6)',
            },
            'input[type=checkbox][aria-invalid=true]+div': {
              borderColor: 'brand.red',
            },
            'input[type=checkbox]:checked+div, input[type=checkbox][aria-checked=mixed]+div': {
              bg: 'brand.purple',
              borderColor: 'brand.purple',
            },
          }"
        >
          <c-checkbox
            id="confirmItemsAreFreeOfDangerousSubstances"
            v-model.trim="requiredFields.confirmItemsAreFreeOfDangerousSubstances"
            @change="onFieldBlur('requiredFields.confirmItemsAreFreeOfDangerousSubstances')"
            data-cy="return-confirm-free-of-dangerous-substances"
            :is-invalid="isErrorField('requiredFields.confirmItemsAreFreeOfDangerousSubstances')"
            size="lg"
            border-radius="input"
          >
            <c-text font-size="md" font-weight="bold" :ml="{ base: 'xs', sm: 'xl' }">
              {{ $t("productReturn.returnDetails.confirmItemsAreFreeOfDangerousSubstances") }}
            </c-text>
          </c-checkbox>
          <c-box
            v-if="isErrorField('requiredFields.confirmItemsAreFreeOfDangerousSubstances')"
            color="brand.red"
            font-size="sm"
            my="xs"
          >
            {{ $t("common.mandatory") }}
          </c-box>
        </c-box>

        <c-heading v-if="isMaintenanceReturn" as="h4" font-size="md" mt="xl">
          {{ beautify($t("productReturn.returnDetails.resetDetails")) }}
        </c-heading>

        <c-text
          v-if="isSalesReturn || isCampaignReturn"
          mt="md"
          font-size="md"
          text-decoration="underline"
        >
          <c-link
            :href="salesReturnPolicyUrl"
            target="_blank"
            @click="(e) => e.stopPropagation()"
            mr="xl"
          >
            {{ $t("productReturnV2.returnPolicyLinkTitle") }}
          </c-link>
        </c-text>
      </c-box>
    </form>
  </c-box>
</template>

<script lang="ts">
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import config from "@/config";
import RMACRMeasurementsPerPackageEditor from "@/views/rma/RMACRMeasurementsPerPackageEditor.vue";
import RMACRWeightPerPackageEditor from "@/views/rma/RMACRWeightPerPackageEditor.vue";
import {
  CBox,
  CButton,
  CCheckbox,
  CFlex,
  CFormControl,
  CFormLabel,
  CHeading,
  CInput,
  CLink,
  CText,
} from "@chakra-ui/vue";
import { ShipperType, IRootState } from "@iloq/shared";
import { defineComponent } from "vue";
import Datepicker from "vuejs-datepicker";
import { decimal, maxValue, minValue, numeric, required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { inputMixin } from "./RMAMixins";

import { datepickerMixin } from "@/util/DatepickerMixin";
import momentTz from "moment-timezone";
import VueI18n from "vue-i18n";

interface IWeightPerPackage {
  weight: number | string;
}

interface IMeasurementsPerPackage {
  length: number | string;
  width: number | string;
  height: number | string;
}

interface IForm {
  copies?: number;
  weight?: number;
  $weightPerPackage: IWeightPerPackage[];
  $measurementsPerPackage: IMeasurementsPerPackage[];
  useImperialUnits: boolean;
}

interface IPackageValidation {
  weightError?: boolean;
  heightError?: boolean;
  widthError?: boolean;
  lengthError?: boolean;
}

interface IPackageValidationWithRowNo extends IPackageValidation {
  rowNo: number;
}

const validateForPosti = {
  form: {
    copies: {
      required,
      numeric,
      minValue: minValue(1),
      maxValue: maxValue(10),
    },
    weight: {
      required,
      decimal,
      minValue: minValue(0.1),
      maxValue: maxValue(500.0),
    },
  },
};

const validateForUPS = {
  form: {
    copies: {
      required,
      numeric,
      minValue: minValue(1),
      maxValue: maxValue(20),
    },
  },
};

const validateForDHL = {
  form: {
    copies: {
      required,
      numeric,
      minValue: minValue(1),
      // limit is 999 in DHL API, but use some reasonable limit here
      maxValue: maxValue(20),
    },
    plannedShippingDate: {
      required,
    },
  },
};

const validateForContactCS = {
  form: {
    copies: { ...validateForDHL.form.copies },
  },
};

const generateRequiredFieldsValidation = (isMaintenanceReturn: boolean, isSalesReturn: boolean) => {
  // this is used for all return types
  const confirmItemsAreFreeOfDangerousSubstances = {
    checked: (v: boolean) => v === true,
  };

  // maintenance return specific
  const confirmResetTried = {
    checked: (v: boolean) => v === true,
  };

  // sales return specific
  const confirmItemsAreInOriginalCondition = {
    checked: (v: boolean) => v === true,
  };

  const validations = { confirmItemsAreFreeOfDangerousSubstances };

  if (isMaintenanceReturn) {
    return {
      ...validations,
      confirmResetTried,
    };
  } else if (isSalesReturn) {
    return {
      ...validations,
      confirmItemsAreInOriginalCondition,
    };
  }
  return validations;
};

export default defineComponent({
  name: "RMA03Details",
  components: {
    RMACRWeightPerPackageEditor,
    RMACRMeasurementsPerPackageEditor,
    CBox,
    CHeading,
    CText,
    CInput,
    CFormLabel,
    CFormControl,
    CFlex,
    CCheckbox,
    CLink,
    CButton,
    Datepicker,
  },
  mixins: [inputMixin, datepickerMixin, FormValidationMixin],
  props: {
    totalProductCount: {
      type: Number,
      required: true,
    },
    shipper: {
      type: String,
      required: true,
    },
    country: {
      type: String,
      required: true,
    },
    isSalesReturn: {
      type: Boolean,
      default: false,
    },
    isMaintenanceReturn: {
      type: Boolean,
      default: false,
    },
    isCampaignReturn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formErrors: [] as any[],
      requiredFields: {
        confirmResetTried: false,
        confirmItemsAreInOriginalCondition: false,
        confirmItemsAreFreeOfDangerousSubstances: false,
      },
      datePickerState: {
        disabledDates: {
          customPredictor: function (date: Date) {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const moreThan10DaysInFuture = new Date();
            moreThan10DaysInFuture.setDate(now.getDate() + 10);
            if (date < now || date > moreThan10DaysInFuture) {
              return true;
            }
            return false;
          },
        },
      },
      form: {
        copies: undefined,
        weight: undefined,
        $weightPerPackage: [] as IWeightPerPackage[],
        $measurementsPerPackage: [] as IMeasurementsPerPackage[],
        useImperialUnits: false,
      } as IForm,
      shipperTypesWithWeightPerPackage: [ShipperType.UPS, ShipperType.DHL, ShipperType.CONTACT_CS],
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    ...mapState({
      isImperialUser: (state: unknown) => (state as IRootState).user?.countryCode === "US",
    }),
    salesReturnPolicyUrl: () => config.get("salesReturnPolicyUrl"),
    useImperialUnits(): boolean {
      return (
        this.isImperialUser &&
        [ShipperType.UPS, ShipperType.DHL, ShipperType.CONTACT_CS].includes(this.shipper)
      );
    },
  },
  validations() {
    const requiredFieldsValidation = {
      requiredFields: generateRequiredFieldsValidation(
        this.isMaintenanceReturn,
        this.isSalesReturn,
      ),
    };

    switch (this.shipper) {
      case ShipperType.POSTI:
        return { ...validateForPosti, ...requiredFieldsValidation };
      case ShipperType.UPS:
        return { ...validateForUPS, ...requiredFieldsValidation };
      case ShipperType.DHL:
        return { ...validateForDHL, ...requiredFieldsValidation };
      case ShipperType.CONTACT_CS:
        return { ...validateForContactCS, ...requiredFieldsValidation };
      case ShipperType.MANUAL:
        return requiredFieldsValidation;
      default:
        return "";
    }
  },
  mounted() {
    this.form = {
      ...this.form,
      copies: this.form?.copies ?? 1,
      $weightPerPackage: this.form?.$weightPerPackage ?? [{ weight: "" }],
      $measurementsPerPackage: this.form?.$measurementsPerPackage ?? [
        { length: "", width: "", height: "" },
      ],
      useImperialUnits: this.useImperialUnits,
    };
  },
  methods: {
    isFirstLineValidAndFilled(): boolean {
      const weight = this.form.$weightPerPackage[0]?.weight;
      const { length, height, width } = this.form.$measurementsPerPackage[0] || {};

      if (!weight || !length || !height || !width) {
        return false;
      }

      const firstPackageValidation = this.singlePackageDetailIsInvalidOrHasErrored(0);
      return !this.isPackageValidationResultInvalidOrErrored(firstPackageValidation);
    },
    singlePackageDetailIsInvalidOrHasErrored(index: number): IPackageValidation {
      const weight = this.$refs[`return_package_weight_${index + 1}`]?.[0]?.$v?.form || {};
      const { height, width, length } =
        this.$refs[`return_package_measurements_${index + 1}`]?.[0]?.$v?.form || {};

      return {
        ...(weight?.$dirty && weight?.$error && { weightError: true }),
        ...(height?.$dirty && height?.$error && { heightError: true }),
        ...(width?.$dirty && width?.$error && { widthError: true }),
        ...(length?.$dirty && length?.$error && { lengthError: true }),
      };
    },
    isPackageValidationResultInvalidOrErrored(packageValidation: IPackageValidation) {
      return Object.keys(packageValidation).length > 0;
    },
    anyPackageDetailAreInvalidOrErrored(): IPackageValidationWithRowNo[] {
      if (!this.form.copies) {
        return [];
      }

      const errors: IPackageValidationWithRowNo[] = [];

      for (let i = 0; i < this.form.copies; i += 1) {
        const packageValidation = this.singlePackageDetailIsInvalidOrHasErrored(i);

        if (this.isPackageValidationResultInvalidOrErrored(packageValidation)) {
          errors.push({
            rowNo: i + 1,
            ...packageValidation,
          });
        }
      }

      return errors;
    },
    getPackageErroredInputs(row: IPackageValidationWithRowNo): (string | VueI18n.LocaleMessages)[] {
      const { weightError, heightError, widthError, lengthError } = row;
      const inputs: (string | VueI18n.LocaleMessages)[] = [];

      if (weightError) {
        inputs.push(this.$t("common.weight"));
      }

      if (lengthError) {
        inputs.push(this.$t("common.measurement.length"));
      }

      if (widthError) {
        inputs.push(this.$t("common.measurement.width"));
      }

      if (heightError) {
        inputs.push(this.$t("common.measurement.height"));
      }

      return inputs;
    },
    copyFirstLineValuesToAllPackages() {
      const weights: IWeightPerPackage[] = [];
      const measurements: IMeasurementsPerPackage[] = [];
      const count = this.limitNumber(this.form.copies, 1, 20, 1);

      for (let i = 0; i < count; i += 1) {
        weights.push({ ...this.form.$weightPerPackage[0] });
        measurements.push({ ...this.form.$measurementsPerPackage[0] });
      }
      this.form.$weightPerPackage = weights;
      this.form.$measurementsPerPackage = measurements;

      for (let i = 0; i < count; i += 1) {
        this.$refs[`return_package_weight_${i + 1}`]?.[0].$v?.form?.$touch();
        this.$refs[`return_package_measurements_${i + 1}`]?.[0].$v?.form?.$touch();
      }
    },
    preparePlannedShippingDate(v: Date): string {
      return momentTz(v).format();
    },
    beautify(text: string): string {
      let t = text.replace(/->/g, "→");
      t = t.replace(/ *, */g, ", ");
      t = t.replace(/ *\. */g, ". ");
      return t.replace(/ *→ */g, " → ").trim();
    },
    limitNumber(t: any, min: number, max: number, d: number): number {
      if (t) {
        const s = `${t}`;
        const v: any = Number(s.replace(/,/g, "."));
        if (!isNaN(v)) {
          if (v < min) {
            return min;
          }
          if (v > max) {
            return max;
          }
          return v;
        }
        return d;
      }
      return t;
    },
    onErrors(index: number, ev: boolean): void {
      this.formErrors[index - 1] = ev;
      this.checkErrors();
    },
    checkErrors(): void {
      if (this.formErrors.indexOf(true) >= 0) {
        this.addFormError("weights", null);
      } else {
        this.removeFormError("weights");
      }
    },
    onCopiesChange(v?: number | string) {
      if (this.form && this.shipperTypesWithWeightPerPackage.includes(this.shipper)) {
        const weights: IWeightPerPackage[] = [];
        const count = this.limitNumber(v, 1, 20, 1);

        if (!this.form.$weightPerPackage) {
          this.form.$weightPerPackage = [];
        }

        for (let i = 0; i < count; i += 1) {
          weights.push(this.form.$weightPerPackage[i] ?? { weight: "" });
        }

        this.form.$weightPerPackage = weights;
      }
      if (this.form && [ShipperType.DHL, ShipperType.CONTACT_CS].includes(this.shipper)) {
        const measurements: IMeasurementsPerPackage[] = [];
        const count = this.limitNumber(v, 1, 20, 1);

        if (!this.form.$measurementsPerPackage) {
          this.form.$measurementsPerPackage = [];
        }

        for (let i = 0; i < count; i += 1) {
          measurements.push(
            this.form.$measurementsPerPackage[i] ?? { length: "", width: "", height: "" },
          );
        }

        this.form.$measurementsPerPackage = measurements;
      }
    },
    submit(): void {
      // Do nothing
    },
  },
  watch: {
    "form.copies": "onCopiesChange",
  },
});
</script>
